/**
 * Product Master v2 — Domain Types and Helpers
 *
 * Built on the normalized database model:
 *   - product_options (stable UUID IDs)
 *   - product_option_values (stable UUID IDs — rename-safe)
 *   - variant_option_values (join table)
 *   - product_attributes
 *   - product_attribute_values
 *   - bundle_components
 *   - product_identifiers
 *
 * The variants.option_values JSONB is now a read-only projection
 * maintained by database triggers. The normalized tables are the
 * source of truth for all option/value identity.
 */

import { dummyTradeIdentifierError, isPlaceholderIdentifier } from "@/lib/catalogue-facts";

// ---------------------------------------------------------------------------
// Option / Value Types (matching DB schema)
// ---------------------------------------------------------------------------

export interface ProductOption {
  id: string;
  org_id: string;
  product_id: string;
  name: string;
  position: number;
  status: "active" | "archived";
  values: ProductOptionValue[];
}

export interface ProductOptionValue {
  id: string;
  org_id: string;
  product_option_id: string;
  value: string;
  position: number;
  status: "active" | "archived";
}

// ---------------------------------------------------------------------------
// Variant Types
// ---------------------------------------------------------------------------

export type ProductType = "physical" | "bundle" | "service";

export type ProductStatus = "draft" | "active" | "paused" | "archived";
export type VariantStatus = "draft" | "active" | "paused" | "archived";

export interface VariantIdentifier {
  id: string;
  type: "barcode" | "gtin" | "upc" | "ean" | "isbn" | "mpn" | "manufacturer_part_number";
  value: string;
}

export interface VariantOverride {
  sku: string | null;
  barcode: string | null;
  identifiers: VariantIdentifier[];
  sellingPrice: number | null;
  costPrice: number | null;
  weightKg: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  status: VariantStatus;
  reorderPoint: number;
  reorderQty: number;
}

/** A canonical variant with its full option assignments using stable IDs */
export interface ProductVariant {
  id: string;
  productId: string;
  orgId: string;
  position: number;
  optionAssignments: Record<string, string>; // optionName -> optionValueId
  overrides: VariantOverride;
}

// ---------------------------------------------------------------------------
// Product Attribute Types
// ---------------------------------------------------------------------------

export type AttributeDataType = "text" | "number" | "boolean" | "date" | "select" | "multiselect";

export interface ProductAttributeDef {
  id: string;
  org_id: string;
  name: string;
  dataType: AttributeDataType;
  options?: string[];
  required: boolean;
  position: number;
}

export interface ProductAttributeValue {
  id: string;
  product_id: string;
  attribute_id: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Bundle / Kit Types
// ---------------------------------------------------------------------------

export interface BundleComponent {
  id: string;
  parent_variant_id: string;
  component_variant_id: string;
  qty: number;
  position: number;
}

// ---------------------------------------------------------------------------
// SKU Normalisation
// ---------------------------------------------------------------------------

/**
 * Canonical sellable SKU lives on the Variant, not the Product.
 *
 * Tradivo deliberately uses a stricter cross-marketplace subset than any
 * individual provider so the same SKU can be reused unchanged across eBay,
 * BigCommerce, Amazon, Temu, and future connectors:
 *   - uppercase ASCII letters, digits, and single hyphen separators only;
 *   - 1–40 characters;
 *   - at least one letter (numeric-only identifiers are not acceptable as
 *     canonical catalogue SKUs);
 *   - case-insensitive uniqueness per organisation.
 *
 * The database trigger mirrors these rules. Marketplace listing IDs, offer IDs,
 * ASINs, item IDs, etc. remain provider identifiers and never replace the SKU.
 */
export const CANONICAL_SKU_MAX_LENGTH = 40;
export const CANONICAL_SKU_PATTERN = /^[A-Z0-9]+(?:-[A-Z0-9]+)*$/;

export function normalizeSku(raw: string): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function skuValidationError(raw: string): string | null {
  const sku = normalizeSku(raw);
  if (!sku) return "SKU is required.";
  if (sku.length > CANONICAL_SKU_MAX_LENGTH) return `SKU must be ${CANONICAL_SKU_MAX_LENGTH} characters or fewer.`;
  if (!/[A-Z]/.test(sku)) return "SKU must contain at least one letter; numeric-only SKUs are not allowed.";
  if (!CANONICAL_SKU_PATTERN.test(sku)) return "SKU may contain only A-Z, 0-9, and single hyphens.";
  return null;
}

export function isValidSku(raw: string): boolean {
  return skuValidationError(raw) === null;
}

function stableSkuHash(value: string): string {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36).toUpperCase().padStart(6, "0").slice(-6);
}

export function fitCanonicalSku(raw: string): string {
  let sku = normalizeSku(raw);
  if (!sku) sku = "SKU";
  if (!/[A-Z]/.test(sku)) sku = `SKU-${sku}`;
  if (sku.length <= CANONICAL_SKU_MAX_LENGTH) return sku;
  const hash = stableSkuHash(sku);
  const prefix = sku.slice(0, CANONICAL_SKU_MAX_LENGTH - hash.length - 1).replace(/-+$/g, "");
  return `${prefix}-${hash}`;
}

/** Stable human-readable option code, including common horse-rug foot/inch sizes. */
export function canonicalOptionSkuCode(value: string): string {
  const source = String(value ?? "").trim().toUpperCase();
  const imperial = source.match(/^(\d+)\s*(?:'|′|FT)\s*(\d{1,2})?\s*(?:\"|″|IN)?$/i);
  if (imperial) return `${imperial[1]}FT${imperial[2] ?? "0"}`;

  const cleaned = source.replace(/[^A-Z0-9]/g, "");
  if (!cleaned) return "OPT";
  if (cleaned.length <= 6) return cleaned;
  return abbreviateForSku(value, 6) || cleaned.slice(0, 6);
}

export function canonicalSkuBase(raw: string): string {
  return fitCanonicalSku(raw || "SKU");
}

/**
 * Builds one canonical variant SKU. Option names are sorted so object insertion
 * order cannot alter identity. A shared `taken` set makes batch generation
 * collision-safe without randomness.
 */
export function buildCanonicalVariantSku(
  base: string,
  optionValues: Record<string, string> | null | undefined,
  taken?: Set<string>,
): string {
  const optionCodes = Object.entries(optionValues ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, value]) => canonicalOptionSkuCode(value))
    .filter(Boolean);
  const candidate = fitCanonicalSku([canonicalSkuBase(base), ...optionCodes].join("-"));
  return taken ? uniqueSku(candidate, taken) : candidate;
}

/**
 * Resolve the effective unit-of-measure for display and pre-fill.
 *
 * The catalogue schema historically carries two near-duplicate columns:
 * `products.unit` (NOT NULL, default 'each') and `products.unit_of_measure`
 * (nullable, default 'each'). Create writes `unit`; edit writes
 * `unit_of_measure`. This helper returns the best available value so an edit
 * form pre-fills with what the operator actually entered instead of showing
 * an empty field.
 */
export function effectiveUnitOfMeasure(unit: string, unitOfMeasure: string | null): string {
  return unitOfMeasure || unit || "each";
}

/**
 * Whether a product is a "simple" product (one sellable SKU) versus a
 * "variation" product (a size/colour/option matrix). The canonical backend
 * variant is unchanged; this drives product-type-aware terminology so a
 * normal operator sees "SKU" instead of the implementation concept "Variant".
 */
export function isSimpleProduct(variantCount: number): boolean {
  return variantCount <= 1;
}

/**
 * Product-type-aware label for the SKU / variant section: "SKU" for a simple
 * product, "Variants" for a variation product.
 */
export function variantSectionLabel(variantCount: number): "SKU" | "Variants" {
  return isSimpleProduct(variantCount) ? "SKU" : "Variants";
}

// ---------------------------------------------------------------------------
// Selling price summary (list-style surfaces)
// ---------------------------------------------------------------------------

export interface SellingPriceSummary {
  /** null when no price is determinable — callers render "—", never "$0.00". */
  min: number | null;
  max: number | null;
}

/**
 * Authoritative selling-price reduction for Overview and list surfaces.
 * Variant `null` inherits the product base. Distinct effective prices become a min–max range.
 */
export function effectiveSellingPrice(
  variantSellingPrice: number | null | undefined,
  productSellingPrice: number | null | undefined,
): number | null {
  if (variantSellingPrice != null) return variantSellingPrice;
  if (productSellingPrice != null) return productSellingPrice;
  return null;
}

export function isVariantPriceOverride(variantSellingPrice: number | null | undefined): boolean {
  return variantSellingPrice != null;
}

/**
 * Canonical cost: NULL variant inherits products.cost_price.
 * A stored numeric 0 is an intentional exception, including A$0.
 */
export function effectiveCostPrice(
  variantCostPrice: number | null | undefined,
  productCostPrice: number | null | undefined,
): number | null {
  return effectiveSellingPrice(variantCostPrice, productCostPrice);
}

export function isVariantCostOverride(variantCostPrice: number | null | undefined): boolean {
  return variantCostPrice != null;
}

/** Empty input inherits. `"0"` / `0.00` stay an explicit zero, not inherit. */
export function parseOptionalNumericInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

export function sellingPriceSummary(
  productSellingPrice: number | null,
  variantSellingPrices: Array<number | null>,
): SellingPriceSummary {
  const prices = variantSellingPrices
    .map((v) => effectiveSellingPrice(v, productSellingPrice))
    .filter((x): x is number => x != null);
  if (prices.length === 0) {
    return productSellingPrice == null ? { min: null, max: null } : { min: productSellingPrice, max: productSellingPrice };
  }
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export function costPriceSummary(
  productCostPrice: number | null,
  variantCostPrices: Array<number | null>,
): SellingPriceSummary {
  return sellingPriceSummary(productCostPrice, variantCostPrices);
}

// ---------------------------------------------------------------------------
// SKU Generation
// ---------------------------------------------------------------------------

const RESERVED_TOKENS = new Set([
  "{SKU}", "{NAME}", "{OPTION}", "{VALUE}", "{INDEX}",
]);

/**
 * Deterministic abbreviation for SKU option tokens.
 *
 * Rules (stable, no randomness):
 *   - uppercase, keep only A–Z and 0–9;
 *   - words of 3 chars or fewer are kept whole (MEN, M, XL, RED);
 *   - longer words prefer their consonant skeleton (BLACK → BLCK, WHITE → WHT)
 *     so "Black" and "Blue" never collide while remaining human-readable;
 *   - fall back to the first `maxLen` chars if the skeleton is too short.
 */
export function abbreviateForSku(value: string, maxLen = 4): string {
  const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (clean.length <= 3) return clean;
  const consonants = clean.replace(/[AEIOU]/g, "");
  if (consonants.length >= 3) return consonants.slice(0, maxLen);
  return clean.slice(0, maxLen);
}

/**
 * Collision-safe SKU resolution. Returns `base` when free, otherwise appends a
 * deterministic `-2`, `-3`, … suffix. Never uses randomness. Mutates `taken`
 * so consecutive calls stay unique within a batch.
 */
export function uniqueSku(base: string, taken: Set<string>): string {
  const clean = fitCanonicalSku(base);
  if (!taken.has(clean)) {
    taken.add(clean);
    return clean;
  }
  let n = 2;
  let out = "";
  do {
    const suffix = `-${n}`;
    const root = clean.slice(0, CANONICAL_SKU_MAX_LENGTH - suffix.length).replace(/-+$/g, "");
    out = `${root}${suffix}`;
    n += 1;
  } while (taken.has(out));
  taken.add(out);
  return out;
}

export function generateSkuFromPattern(
  pattern: string,
  context: {
    productSku?: string | null;
    productName?: string;
    optionCodes: Record<string, string>;
  },
  index?: number,
): string {
  let sku = pattern.toUpperCase();
  if (context.productSku) {
    sku = sku.replace(/\{SKU\}/g, context.productSku.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12));
  }
  if (context.productName) {
    sku = sku.replace(/\{NAME\}/g, context.productName.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8));
  }
  for (const [optName, code] of Object.entries(context.optionCodes)) {
    const token = `{${optName.toUpperCase().replace(/[^A-Z0-9]/g, "")}}`;
    sku = sku.replace(new RegExp(token, "g"), code.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6));
  }
  if (index != null) {
    sku = sku.replace(/\{INDEX\}/g, String(index + 1));
  }
  sku = sku.replace(/[^A-Z0-9\-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  return fitCanonicalSku(sku || "SKU");
}

export function validateSkuPattern(pattern: string, optionNames: string[]): string | null {
  if (!pattern.trim()) return "Pattern is required.";
  const tokens = pattern.match(/\{[A-Z0-9_]+\}/gi) ?? [];
  for (const t of tokens) {
    const clean = t.replace(/\{|\}/g, "").toUpperCase();
    if (!RESERVED_TOKENS.has(t.toUpperCase()) && !optionNames.some((n) => n.toUpperCase().replace(/[^A-Z0-9]/g, "") === clean)) {
      return `Unknown token ${t}. Available: {SKU}, {NAME}, {INDEX}, or option names.`;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Combination Generation
// ---------------------------------------------------------------------------

export interface CombinationRow {
  values: string[];
  optionAssignments: Record<string, string>;
  sku: string;
  barcode: string | null;
  included: boolean;
  price: number | null;
  cost: number | null;
  weightKg: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  qty?: number | null;
  status: VariantStatus;
}

export function cartesianProduct<T>(arrays: T[][]): T[][] {
  return arrays.reduce<T[][]>((acc, arr) => acc.flatMap((combo) => arr.map((v) => [...combo, v])), [[]]);
}

export function generateCombinations(
  options: ProductOption[],
  pattern: string,
  context: { productSku?: string | null; productName?: string },
  existingSkus: string[],
  startingIndex: number,
): CombinationRow[] {
  if (options.length === 0) return [];
  const arrays = options.map((o) => o.values.map((v) => ({ value: v.value, valueId: v.id, optionName: o.name })));
  const product = cartesianProduct(arrays);

  const used = new Set(existingSkus.map((s) => s.toUpperCase()));

  return product.map((combo, idx) => {
    const optionAssignments: Record<string, string> = {};
    const codes: Record<string, string> = {};
    const values: string[] = [];
    for (const item of combo) {
      optionAssignments[item.optionName] = item.valueId;
      values.push(item.value);
      codes[item.optionName] = canonicalOptionSkuCode(item.value);
    }
    const baseSku = generateSkuFromPattern(pattern, { ...context, optionCodes: codes }, idx);
    const sku = uniqueSku(baseSku, used);
    return {
      values,
      optionAssignments,
      sku,
      barcode: null,
      included: true,
      price: null,
      cost: null,
      weightKg: null,
      lengthCm: null,
      widthCm: null,
      heightCm: null,
      qty: null,
      status: "active" as VariantStatus,
    };
  }).slice(startingIndex);
}

export function countCombinations(options: ProductOption[]): number {
  if (options.length === 0) return 0;
  return options.reduce((acc, o) => acc * Math.max(1, o.values.length), 1);
}

export function combinationSafety(options: ProductOption[]): { safe: boolean; count: number; warning?: string } {
  const count = countCombinations(options);
  if (count === 0) return { safe: true, count: 0 };
  if (count > 10000) return { safe: false, count, warning: `This would create ${count.toLocaleString()} variants. The maximum safe batch is 10,000. Reduce option values or select fewer combinations.` };
  if (count > 1000) return { safe: true, count, warning: `Warning: ${count.toLocaleString()} combinations. Generation may take a moment.` };
  return { safe: true, count };
}

// ---------------------------------------------------------------------------
// Uniqueness
// ---------------------------------------------------------------------------

export function findDuplicateCombinations(
  combinations: CombinationRow[],
): { indexA: number; indexB: number; assignments: Record<string, string> }[] {
  const seen = new Map<string, number>();
  const dups: { indexA: number; indexB: number; assignments: Record<string, string> }[] = [];
  for (let i = 0; i < combinations.length; i++) {
    const c = combinations[i];
    if (!c.included) continue;
    const key = JSON.stringify(c.optionAssignments, Object.keys(c.optionAssignments).sort());
    if (seen.has(key)) {
      dups.push({ indexA: seen.get(key)!, indexB: i, assignments: c.optionAssignments });
    } else {
      seen.set(key, i);
    }
  }
  return dups;
}

export function findDuplicateSkus(
  combinations: CombinationRow[],
  existingSkus: string[],
): { index: number; sku: string }[] {
  // Compare on the canonical SKU contract, not raw text. Submission normalises
  // through normalizeSku(), so "BLK M" and "BLK-M" both submit as "BLK-M".
  // Detecting duplicates on the raw uppercase string would let that collision
  // reach the database.
  const all = new Set(existingSkus.map((s) => canonicalSkuKey(s)));
  const dups: { index: number; sku: string }[] = [];
  for (let i = 0; i < combinations.length; i++) {
    const c = combinations[i];
    if (!c.included) continue;
    const key = canonicalSkuKey(c.sku);
    if (all.has(key)) dups.push({ index: i, sku: c.sku });
    else all.add(key);
  }
  return dups;
}

/** One canonical comparison key for duplicate/conflict detection. */
export function canonicalSkuKey(raw: string): string {
  const normalized = normalizeSku(raw);
  return normalized || String(raw ?? "").trim().toUpperCase();
}

/**
 * Collisions that only appear AFTER canonicalisation ("BLK M" vs "BLK-M").
 * Index points at the later row so the caller can name the offending entry
 * without rewriting either operator-authored SKU.
 */
export function findCanonicalSkuCollisions(skus: string[]): { index: number; sku: string; conflictsWith: string }[] {
  const seen = new Map<string, string>();
  const out: { index: number; sku: string; conflictsWith: string }[] = [];
  skus.forEach((sku, index) => {
    const key = canonicalSkuKey(sku);
    if (!key) return;
    const first = seen.get(key);
    if (first !== undefined) out.push({ index, sku, conflictsWith: first });
    else seen.set(key, sku);
  });
  return out;
}

/** Canonical, de-duplicated SKU list suitable for an org-scoped existence check. */
export function canonicalSkuList(skus: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const sku of skus) {
    const canonical = normalizeSku(sku);
    if (!canonical || seen.has(canonical)) continue;
    seen.add(canonical);
    out.push(canonical);
  }
  return out;
}

/**
 * Detect collisions between proposed SKUs and already-existing SKUs (compared
 * case-insensitively). Returns the proposed SKUs that already exist, so a
 * caller can surface a friendly pre-submit warning before the DB's unique
 * constraint rejects the write.
 */
export function findSkuConflicts(proposed: string[], existing: string[]): string[] {
  const taken = new Set(existing.map((s) => canonicalSkuKey(s)).filter(Boolean));
  return proposed.filter((s) => {
    const k = canonicalSkuKey(s);
    return k.length > 0 && taken.has(k);
  });
}

// ---------------------------------------------------------------------------
// Attribute Helpers
// ---------------------------------------------------------------------------

export function formatAttributeValue(dataType: AttributeDataType, raw: string): string {
  if (dataType === "boolean") return raw === "true" || raw === "1" ? "Yes" : "No";
  if (dataType === "number") {
    const n = Number(raw);
    return Number.isNaN(n) ? raw : n.toLocaleString();
  }
  return raw;
}

export function validateAttributeValue(dataType: AttributeDataType, raw: string): string | null {
  if (dataType === "number" && raw !== "" && Number.isNaN(Number(raw))) {
    return "Must be a number.";
  }
  if (dataType === "boolean" && raw !== "" && raw !== "true" && raw !== "false" && raw !== "1" && raw !== "0") {
    return "Must be true/false/1/0.";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Identifier Validation (mirrors DB validate_standard_identifier)
// ---------------------------------------------------------------------------

/**
 * Validates standard retail identifier check digits (GTIN/UPC/EAN/ISBN).
 * Non-standard types (barcode, mpn, manufacturer_part_number) are not validated.
 * Returns null when valid (or empty), otherwise a human-readable error message.
 */
export function validateIdentifierValue(type: string, raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;

  if (type === "mpn" || type === "manufacturer_part_number") {
    if (isPlaceholderIdentifier(v)) return "placeholder is not a real MPN — leave blank if unknown";
    return null;
  }

  if (type === "gtin" || type === "upc" || type === "ean") {
    const dummy = dummyTradeIdentifierError(v);
    if (dummy) return dummy;
    if (!/^\d+$/.test(v)) return "must contain digits only";
    if (type === "upc" && v.length !== 12) return "UPC must be 12 digits";
    if (type === "ean" && v.length !== 13) return "EAN must be 13 digits";
    if (type === "gtin" && ![8, 12, 13, 14].includes(v.length)) return "GTIN must be 8, 12, 13 or 14 digits";
    const body = v.slice(0, -1);
    const cd = Number(v[v.length - 1]);
    let sum = 0;
    for (let i = 0; i < body.length; i++) {
      const ch = Number(body[body.length - 1 - i]);
      const w = i % 2 === 0 ? 3 : 1;
      sum += ch * w;
    }
    if ((10 - (sum % 10)) % 10 !== cd) return "invalid check digit";
  } else if (type === "isbn") {
    const clean = v.replace(/[- ]/g, "");
    if (clean.length === 13) {
      if (!/^\d+$/.test(clean)) return "ISBN-13 must contain digits only";
      const body = clean.slice(0, 12);
      const cd = Number(clean[12]);
      let sum = 0;
      for (let i = 0; i < 12; i++) {
        const ch = Number(body[i]);
        const w = i % 2 === 0 ? 1 : 3;
        sum += ch * w;
      }
      if ((10 - (sum % 10)) % 10 !== cd) return "invalid ISBN-13 check digit";
    } else if (clean.length === 10) {
      let sum = 0;
      for (let i = 0; i < 10; i++) {
        const ch = clean[i] === "X" || clean[i] === "x" ? 10 : Number(clean[i]);
        if (Number.isNaN(ch)) return "invalid ISBN-10";
        sum += ch * (10 - i);
      }
      if (sum % 11 !== 0) return "invalid ISBN-10 check digit";
    } else {
      return "ISBN must be 10 or 13 digits";
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Product Extended Fields (matching DB schema)
// ---------------------------------------------------------------------------

export interface ProductMasterV2 {
  id: string;
  orgId: string;
  name: string;
  /** Internal product reference code (catalogue/commercial identity) — NOT the sellable SKU. */
  reference: string | null;
  /** @deprecated Legacy product-level SKU mirror. Canonical sellable SKU is on the Variant (`variants.sku`). */
  sku: string | null;
  barcode: string | null;
  categoryId: string | null;
  description: string | null;
  status: ProductStatus;
  unit: string;
  costPrice: number | null;
  sellingPrice: number | null;
  reorderPoint: number;
  reorderQty: number;
  weightKg: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  productType: ProductType;
  brand: string | null;
  manufacturer: string | null;
  modelNumber: string | null;
  manufacturerPartNumber: string | null;
  countryOfOrigin: string | null;
  hsTariffCode: string | null;
  customsDescription: string | null;
  unitOfMeasure: string | null;
  taxProfile: string | null;
  shippingProfile: string | null;
  condition: string | null;
}
